import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/v5/lib/api";

export interface Lead {
  contactID: string;
  contactDate: string;
  firstName: string;
  lastName: string;
  /** Short subject label (legacy contacts.topics). */
  subject: string;
  /** Pre-delimiter portion of contacts.message — short intro text, safe to show on a card. */
  messagePreview?: string;
  origin: string;
  leadFrom: string;
  leadSource: string;
  utmSource: string;
  utmCampaign: string;
  interest: string;
  planType: string;
  status: string;
  statusID: string;
  assignedTo: string;
  contactEmail: string;
  contactPhone: string;
}

interface LaravelLeadsResponse {
  data: { leads: Lead[]; totalCount: number };
  meta: unknown;
}

const PAGE_SIZE = 25;

export function useCRMLeads(enabled = true, filtersArray = "", filterPlus = "") {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const fetchingRef = useRef(false);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (start: number, append: boolean) => {
      // The in-flight guard exists to stop the intersection observer firing
      // `loadMore` twice and appending the same page — so it only applies to an
      // append. A RESET fetch carries new filters, and dropping it is what left
      // this list showing unfiltered rows: saved filters land one round-trip
      // after mount, while the mount's own unfiltered request is still open.
      if (append && fetchingRef.current) return;
      fetchingRef.current = true;
      // Two resets can now overlap, so the newest request wins regardless of
      // which response arrives first.
      const requestId = ++requestIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const body: Record<string, unknown> = {
          displayStart: start,
          displayLength: PAGE_SIZE,
          filtersArray,
        };
        if (filterPlus) body.filterPlus = filterPlus;
        const result = await api<LaravelLeadsResponse>("/crm/leads", {
          method: "POST",
          body,
        });
        if (requestId !== requestIdRef.current) return;
        if (result.data?.leads) {
          setLeads((prev) => (append ? [...prev, ...result.data.leads] : result.data.leads));
          setTotalCount(result.data.totalCount ?? 0);
          offsetRef.current = start + result.data.leads.length;
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to fetch CRM leads:", err);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
          fetchingRef.current = false;
        }
      }
    },
    [filtersArray, filterPlus],
  );

  const loadMore = useCallback(() => {
    fetchPage(offsetRef.current, true);
  }, [fetchPage]);

  const refetch = useCallback(() => {
    offsetRef.current = 0;
    setLeads([]);
    fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    if (!enabled) return;
    offsetRef.current = 0;
    setLeads([]);
    setTotalCount(0);
    fetchPage(0, false);
  }, [enabled, fetchPage]);

  const hasMore = leads.length < totalCount;

  return { leads, totalCount, loading, loadingMore, hasMore, loadMore, refetch };
}
